import { tacticalApi, type TacticalFeature } from "@/services/tactical.service";
import {
  deleteMutation,
  listMutations,
  putBoardFeaturesCache,
  remapQueueFeatureId,
  type TacticalOfflineMutation,
} from "./tacticalOfflineDb";

export type FlushResult = {
  flushed: number;
  remaining: number;
  error?: string;
};

/**
 * FIFO flush очереди доски через REST. Stop on first error.
 */
export async function flushTacticalQueue(boardId: number): Promise<FlushResult> {
  let flushed = 0;
  let queue: TacticalOfflineMutation[] = await listMutations(boardId);

  while (queue.length > 0) {
    const item = queue[0]!;
    try {
      if (item.op === "create") {
        const created = await tacticalApi.createFeature(boardId, item.payload ?? {});
        const serverId = Number(created.properties?.id ?? created.id);
        await deleteMutation(item.id);
        flushed += 1;
        if (Number.isFinite(serverId) && item.featureId < 0) {
          await remapQueueFeatureId(boardId, item.featureId, serverId);
        }
      } else if (item.op === "update") {
        if (item.featureId < 0) {
          await deleteMutation(item.id);
          flushed += 1;
        } else {
          await tacticalApi.updateFeature(boardId, item.featureId, item.payload ?? {});
          await deleteMutation(item.id);
          flushed += 1;
        }
      } else if (item.op === "delete") {
        if (item.featureId < 0) {
          await deleteMutation(item.id);
          flushed += 1;
        } else {
          await tacticalApi.deleteFeature(boardId, item.featureId);
          await deleteMutation(item.id);
          flushed += 1;
        }
      }
    } catch (e) {
      const remaining = (await listMutations(boardId)).length;
      return {
        flushed,
        remaining,
        error: e instanceof Error ? e.message : "Ошибка sync очереди",
      };
    }
    queue = await listMutations(boardId);
  }

  try {
    const fc = await tacticalApi.listFeatures(boardId);
    await putBoardFeaturesCache(boardId, (fc.features ?? []) as TacticalFeature[]);
  } catch {
    /* cache refresh optional after flush */
  }

  return { flushed, remaining: 0 };
}
